import { mapGetters, mapState, mapActions } from 'vuex'
import { cloneDeep, intersection, uniq, range, values } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'

import { getCultivationActionPeriod, canCultivationStartAt, canCultivationStartThatYearFrom, canCropStartAt, canCropStartThatYearFrom } from '../../helpers/crops'
import types from '../../store/globaltypes'
import card from '../../components/card'
import infoline from '../../components/infoline'
import timeline from '../../components/timeline'

export default {
  name: 'wizard',
  components: { card, infoline, timeline },
  props: [],
  created () {
    const tryRoute = () => {
      if (this.crops && this.crops.length) this.processRoute()
      else setTimeout(tryRoute, 250)
    }
    setTimeout(tryRoute, 250)
  },
  data () {
    return {
      cropToContinue: null,
      cropToContinueDate: null,
      cropListFilter: [],
      cropGroupFilter: null,
      cropFilter: 'all',
      cultivationFilter: 'all',
      startingDate: null,
      cropSearch: '',
      method: null,
      cropID: null,
      hasSelectedDate: false,
      hasAddedToPlanning: false
    }
  },
  methods: {
    ...mapActions({
      addPlanningItem: types.planning.actions.add,
      reloadPlanning: types.planning.actions.reload,
      showInfo: types.main.actions.setInformation,
      addToCart: types.shoppingcart.actions.addItemToCart,
      loadProducts: types.products.actions.loadList,
      filterCrops: types.crops.actions.filter
    }),
    isDayAllowed (date) {
      if (!this.period) return false
      const dateToCheckFrom = this.moment(date).set({ year: this.period.from.year() })
      const dateToCheckTo = this.moment(date).set({ year: this.period.to.year() })
      const from = dateToCheckFrom.isSameOrAfter(this.period.from) && dateToCheckFrom.isSameOrBefore(this.period.to)
      const to = dateToCheckTo.isSameOrAfter(this.period.from) && dateToCheckTo.isSameOrBefore(this.period.to)
      return from || to
    },
    savePlanning () {
      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Crop added (via wizard)',
        eventLabel: `${this.actionsToAdd.length} events`
      })
      this.actionsToAdd.forEach(action => {
        this.addPlanningItem(action)
        this.$ga.event({
          eventCategory: 'Planning',
          eventAction: 'Task added'
        })
      })
      this.showInfo(`Teelt toegevoegd aan planning`)
      this.hasAddedToPlanning = true
      this.reloadPlanning()
    },
    selectDate () {
      this.hasSelectedDate = true
    },
    addProductToCart (productID, variationID) {
      this.addToCart({
        productID,
        cropID: this.cropID,
        variationID: variationID
      })
      this.$ga.event({
        eventCategory: 'Product',
        eventAction: 'Product added to wishlist',
        eventLabel: `Added from page ${this.$route.name}`
      })
    },
    selectThisCrop (item) {
      this.cropID = item.key
    },
    chooseDate (item) {
      this.method = item.ID
      if (this.period.from.isBefore() && this.period.to.isAfter()) this.startingDate = this.moment().format('YYYY-MM-DD')
      else this.startingDate = this.period.from.format('YYYY-MM-DD')
    },
    ready () {
      this.cropID = null
      this.method = null
      this.hasSelectedDate = false
      this.hasAddedToPlanning = false
    },
    processRoute () {
      this.ready()
      this.cropListFilter = []
      if (this.$route.query.continuedcrops) {
        this.cropToContinue = this.cropByName(this.$route.query.continuedcrops)
        if (this.cropToContinue) {
          this.cropToContinueDate = moment(this.$route.query.from)
          this.cropFilter = 'continued-crops'
          this.cropListFilter = this.cropToContinue.continued_crops
        }
      }
      if (this.$route.query.cropname) this.cropID = this.cropByName(this.$route.query.cropname).key
      if (this.$route.query.method) this.chooseDate({ ID: this.$route.query.method })
      this.updateCropGroupFilterResults()
    },
    updateCropGroupFilterResults () {
      let index = this.cropGroups.indexOf(this.cropGroupFilter)
      if (index === -1) this.filterCrops(null)
      else this.filterCrops(this.cropGroupFilter)
    },
    toThisYear () {
      const thisYear = new Date().getFullYear().toString()
      const nextYear = (new Date().getFullYear() + 1).toString()
      this.startingDate = this.startingDate.replace(nextYear, thisYear)
    }
  },
  computed: {
    ...mapState({
      isLoading: state => !state.ui.crops.isDataLoaded
    }),
    ...mapGetters({
      cropByName: types.crops.getters.crop,
      crops: types.crops.getters.crops,
      cropGroups: types.crops.getters.cropgroups,
      cropGetter: types.crops.getters.cropById,
      groupedProducts: types.products.getters.groupByCategory,
      multiLanguageCropById: types.crops.getters.multiLanguageCropById,
      locale: types.user.getters.localeCode,
      translatePostID: types.i18n.getters.translatePostID,
      filteredCrops: types.crops.getters.filteredCrops
    }),
    cropGroupFilters () {
      return [ this.$t('crops.all_crops'), ...this.cropGroups ]
    },
    cropFilterTab: {
      get () {
        return this.cropFilter === 'all' ? 0 : 1
      },
      set (val) {
        if (val === 0) {
          this.cropFilter = 'all'
          this.cropListFilter = []
          this.cropToContinue = null
        } else {
          this.cropFilter = this.cropListFilter.length ? 'continued_crops' : 'right_now'
        }
      }
    },
    cultivationFilterTab: {
      get () {
        return this.cultivationFilter === 'all' ? 0 : 1
      },
      set (val) {
        this.cultivationFilter = val === 0 ? 'all' : 'right_now'
      }
    },
    crop () {
      if (!this.cropID) return null
      return this.cropGetter(this.cropID)
    },
    currentStep: {
      get: function () {
        if (!this.cropID) return 1
        if (!this.method) return 2
        if (!this.hasSelectedDate) return 3
        if (!this.hasAddedToPlanning) return 4
        return 5
      },
      set: function (newStep, x) {
        // ----- Hack to get around vuetify v-tab in v-stepper bug -----
        if (newStep == 2) {
          setTimeout(() => {
            this.cultivationFilter = this.cropFilter === 'all' ? 'right_now' : 'all'
            setTimeout(() => { this.cultivationFilter = this.cropFilter }, 1)
          }, 1)
        }
        // ------
        if (newStep !== this.currentStep) {
          newStep = parseInt(newStep)
          if (newStep <= 4) this.hasAddedToPlanning = false
          if (newStep <= 3) this.hasSelectedDate = false
          if (newStep <= 2) this.method = null
          if (newStep <= 1) this.cropID = null
        }
      }
    },
    title () {
      return (index, step) => {
        return this.crop[`method${index}action${step}_action_type`]
      }
    },
    icon () {
      return (index, step) => {
        var icon = 'fa-seedling'
        // const actiontype = this.actionType(index, step)
        // if (actiontype) {
        //   switch (actiontype.toLowerCase()) {
        //     case 'zaaien':
        //       icon = 'fa-allergies'
        //       break
        //     case 'voorzaaien':
        //       icon = 'fa-allergies'
        //       break
        //     case 'verspenen':
        //       icon = 'fa-hands'
        //       break
        //     case 'grondvoorbereiding':
        //       icon = 'fa-swimmer'
        //       break
        //   }
        // }
        return icon
      }
    },
    actionsToAdd () {
      if (!this.crop || !this.method || !this.hasSelectedDate) return []
      let res = []

      const ID = uuidv4()
      for (var step = 1; step <= parseInt(this.crop[`method${this.method}_number_of_actions`]); step++) {
        const period = this.actionPeriod(step)
        var absoluteDate = null
        if (step === 1) {
          absoluteDate = this.moment(this.startingDate, 'YYYY-MM-DD')
        } else if (period.relative) {
          const relatedStep = cloneDeep(res.filter(x => x.stepID === parseInt(period.action))[0])
          absoluteDate = relatedStep.period.add(period.amount, period.period)
        }

        res.push({
          ID,
          cropID: this.crop['key'],
          methodID: this.method,
          stepID: step,
          title: `${this.crop['post_title']}: ${this.title(this.method, step)} (${this.crop[`method${this.method}_methodname`]})`,
          icon: this.icon(this.method, step),
          period: absoluteDate
        })
      }

      return res
    },
    actionType () {
      return (index, step) => (this.crop[`method${index}action${step}_action_type`])
    },
    period () {
      if (!this.method || !this.crop) return null
      return getCultivationActionPeriod(this.crop, this.method, 1)
    },
    actionPeriod () {
      return (step) => {
        if (!this.method || !this.crop) return null
        return getCultivationActionPeriod(this.crop, this.method, step)
      }
    },
    showNextYearWarning () {
      if (!this.method || !this.crop || !this.period) return false
      return this.period.to.isBefore(this.moment().endOf('day'))
    },
    productIDs () {
      if (!this.crop || !this.method) return []
      let productIDs = []
      for (let step = 1; step <= parseInt(this.crop['number_of_methods']); step++) {
        let prods = this.crop[`method${this.method}action${step}_linked_products`]
        if (prods) {
          prods = prods.split(';')
          prods.pop()
          const regex = new RegExp(/\[(.*)\]/)
          prods.forEach(prod => {
            if (regex.test(prod) && prod.match(regex).length > 0) {
              productIDs.push(prod.match(/\[(.*)\]/)[1])
            }
          })
        }
      }
      return uniq(productIDs)
    },
    linkedProds () {
      if (!this.crop || !this.method) return []
      return this.groupedProducts(this.productIDs)
    },
    searchedAndFilteredCrops () {
      return this.filteredCrops
        .filter(x => x.post_title.toLowerCase().includes(this.cropSearch.toLowerCase()))
        .filter(x => {
          if (this.cropFilter === 'right_now') {
            return canCropStartAt(x, moment())
          } else if (this.cropFilter === 'continued-crops') {
            let continuedCrops = intersection(values(x.postIDs || [x.key]), this.cropListFilter).map(x => this.cropGetter(x))
            continuedCrops = continuedCrops.filter(x => canCropStartThatYearFrom(x, this.cropToContinueDate))
            return continuedCrops.length > 0
          }
          return true
        })
    },
    cultivationMethods () {
      if (!this.crop) return []
      const indices = range(1, parseInt(this.crop['number_of_methods']) + 1)
      return indices
        .filter(i => {
          if (this.cropToContinue) {
            return canCultivationStartThatYearFrom(this.crop, i, this.cropToContinueDate)
          }
          return this.cultivationFilter === 'all' || canCultivationStartAt(this.crop, i, moment())
        })
        .map(i => {
          return {
            ID: i,
            title: this.crop[`method${i}_methodname`] || `Method ${i}`,
            days: this.crop[`method${i}_growing_cycle`] || 0,
            crop: this.crop
          }
        })
    }
  },
  watch: {
    $route (to) {
      if (to.name === 'wizard') {
        this.processRoute()
      }
    },
    locale () {
      this.cropID = this.translatePostID(this.cropID)
      if (this.method) this.loadProducts(this.productIDs)
    },
    cropGroupFilter () {
      this.updateCropGroupFilterResults()
    },
    method: function () {
      this.loadProducts(this.productIDs)
    }
  }
}
